import { useSelector } from 'react-redux';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { PAGE_CODES } from 'constants/pageCodes';
import useIsCrmEnabled from 'hooks/useIsCrmEnabled';

import { getMasterPermissionsToPage, selectIsClaimAllowed } from 'store/auth/selectors';

/**
 * Resolves which top-level objects the current user is permitted to create from the
 * mobile "+" flow. Mirrors the per-object gating the pipelines toolbar applies, but
 * evaluated globally (no pipeline `representationViewType` dependency).
 *
 * - Loan: the import-MISMO claim (the existing manual loan-create path).
 * - Lead: the manual-lead-creation claim.
 * - Contact: the CRM feature flag AND view access to the CRM Contacts page.
 *
 * @returns {{ canCreateLoan: boolean, canCreateLead: boolean, canCreateContact: boolean }}
 */
const useCreatableObjects = () => {
  const canCreateLoan = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_PIPELINES_IMPORT_MISMO),
  );
  const canCreateLead = useSelector(selectIsClaimAllowed(COMPOSED_CLAIMS.MANUAL_LEAD_CREATION));

  const isCrmEnabled = useIsCrmEnabled();
  const crmContactsPermission = useSelector(getMasterPermissionsToPage(PAGE_CODES.CRM_CONTACTS));
  const canCreateContact = isCrmEnabled && !!crmContactsPermission?.isViewAllowed;

  return { canCreateLoan, canCreateLead, canCreateContact };
};

export default useCreatableObjects;
